import { useAuth } from "@/providers/auth-provider";
// import { useSignals } from "@preact/signals-react/runtime";
import { urlBase64ToUint8Array } from "@/lib/web-tools";
import { withRetry } from "@/lib/utils";
import { useEffect } from "react";
import { apiRegisterSubscription } from "@/lib/client-api";

const VAPID_KEY = urlBase64ToUint8Array(import.meta.env.VITE_PUBLIC_VAPID_KEY!);

const SUBSCRIPTION_CONFIG = {
  EXPIRATION: {
    CRITICAL: 3 * 24 * 60 * 60 * 1000,
    WARNING: 7 * 24 * 60 * 60 * 1000,
    SAFE: 14 * 24 * 60 * 60 * 1000,
  },
  RETRY: {
    MAX_ATTEMPTS: 3,
    DELAY_MS: 1000,
  },
};

export async function ensureNotificationPermission() {
  // 현재 알림 권한 상태 확인
  const permission = Notification.permission;

  switch (permission) {
    case "granted":
      // 이미 권한이 허용된 상태
      console.log("알림 권한이 허용된 상태입니다.");
      return true;

    case "denied":
      // 권한이 거절된 상태 - 브라우저 설정에서 직접 변경하도록 안내
      console.warn(
        "알림 권한이 거절되어 있습니다. 브라우저 설정을 변경해주세요.",
      );
      alert(`
        알림 권한이 차단되어 있습니다.
        알림을 받으시려면 브라우저 설정에서 권한을 변경해주세요:

        1. 브라우저 주소창의 자물쇠/느낌표 아이콘을 클릭
        2. 알림 설정을 "허용"으로 변경
      `);
      return false;

    case "default":
      // 아직 사용자가 선택하지 않은 상태
      try {
        const result = await Notification.requestPermission();
        console.log("알림 권한 요청 결과:", result);
        return result === "granted";
      } catch (error) {
        console.error("알림 권한 요청 실패:", error);
        return false;
      }
  }
}

// 개선된 메인 구독 함수
export async function registerPushSubscription(user: any) {
  try {
    const subscription =
      (await validatePushSubscription()) || (await createNewSubscription());

    if (!subscription) throw new Error("Failed to create subscription");

    await withRetry(
      () => sendSubscriptionToServer(subscription, user),
      SUBSCRIPTION_CONFIG.RETRY.MAX_ATTEMPTS,
      SUBSCRIPTION_CONFIG.RETRY.DELAY_MS,
      true,
    );
  } catch (error) {
    console.error("Push subscription failed:", error);
    throw error;
  }
}

// 구독 체크 함수
async function validatePushSubscription() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  if (!subscription) return null;

  // Firefox의 경우에만 만료시간 체크
  if (navigator.userAgent.includes("Firefox")) {
    const timeLeft = subscription.expirationTime
      ? subscription.expirationTime - Date.now()
      : 0;
    const threshold = SUBSCRIPTION_CONFIG.EXPIRATION.CRITICAL;
    if (timeLeft < threshold) {
      console.log("Subscription renewal needed:", {
        browser: navigator.userAgent,
        timeLeft: Math.floor(timeLeft / (24 * 60 * 60 * 1000)), // 남은 일수
        threshold: Math.floor(threshold / (24 * 60 * 60 * 1000)), // 임계값 일수
        subscription,
      });
      await subscription.unsubscribe();
      return null;
    }
  }

  return subscription;
}

// 새 구독 생성 헬퍼 함수
async function createNewSubscription() {
  const registration = await navigator.serviceWorker.ready;
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: VAPID_KEY as unknown as BufferSource,
  });
}

// 서버 전송 헬퍼 함수
async function sendSubscriptionToServer(
  subscription: PushSubscription,
  user: any,
) {
  const result = await apiRegisterSubscription(
    {
      // userId: user._id, // 유저 정보는 token에 포함되어 있으므로 제외.
      deviceId: user.deviceId,
      subscription: subscription,
    },
    user.token,
  );
  console.log("Push subscription registered:", result);
  return result;
}

// 알림 권한 변경 감지 및 자동 구독 함수
async function setupPermissionChangeListener(user: any) {
  if (!("permissions" in navigator)) {
    console.warn("Permissions API를 지원하지 않는 브라우저입니다.");
    return;
  }

  try {
    const permissionStatus = await navigator.permissions.query({
      name: "notifications" as PermissionName,
    });

    console.log(
      "[PWA-Subscription] 알림 권한 상태 감지 시작:",
      permissionStatus.state,
    );

    // 권한 상태 변경 감지
    permissionStatus.onchange = async () => {
      console.log(
        "[PWA-Subscription] 알림 권한이 변경되었습니다:",
        permissionStatus.state,
      );

      // 권한이 허용된 경우 자동으로 구독 등록
      if (permissionStatus.state === "granted") {
        console.log(
          "[PWA-Subscription] 권한이 허용됨. 자동으로 구독을 등록합니다.",
        );
        try {
          await registerPushSubscription(user);
          console.log("[PWA-Subscription] 구독이 성공적으로 등록되었습니다.");
        } catch (error) {
          console.error("[PWA-Subscription] 자동 구독 등록 실패:", error);
        }
      } else if (permissionStatus.state === "denied") {
        console.warn("[PWA-Subscription] 권한이 거부되었습니다.");
      }
    };
  } catch (error) {
    console.error("[PWA-Subscription] 권한 상태 감지 설정 실패:", error);
  }
}

export function PWASubscription() {
  const { user } = useAuth();

  useEffect(() => {
    if (!import.meta.env.PROD || !user?._id) return;

    // 초기 구독 시도
    (async () => {
      if (await ensureNotificationPermission()) {
        await registerPushSubscription(user);
      }
    })();

    // 권한 변경 감지 리스너 설정
    setupPermissionChangeListener(user);
  }, [user?._id]);

  return null;
}
